import React from 'react';
import { ExternalLink, PlayCircle, Download, MessageCircle } from 'lucide-react';
import { API_BASE, api } from '@/lib/api';
import { toast } from 'sonner';

const MEAL_TINTS = {
  'Morning Drink': '#EFE4D2',
  'Breakfast': '#E4EAD8',
  'Mid-Morning': '#E9E1CE',
  'Lunch': '#EAD8CE',
  'Evening Snack': '#DCE0D0',
  'Dinner': '#E7DAC9',
  'Bedtime': '#DCD3E0',
};

const isYouTube = (url = '') => /youtu(be)?\./i.test(url);

const downloadPdf = async (planId) => {
  try {
    const res = await api.get(`/diet/plans/${planId}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `diet-plan-${planId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error('Download failed');
  }
};

const DietPlanView = ({ plan }) => {
  if (!plan) return null;
  const nonEmpty = (plan.sections || []).filter((s) => (s.dishes || []).length > 0);

  return (
    <div className="bg-sans_bone">
      {/* Header */}
      <div className="border border-sans_line bg-white p-8 md:p-10">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="sans-label mb-3" style={{ color: '#8A9A5B' }}>Your personalised diet</div>
            <div className="font-serif text-3xl md:text-4xl text-sans_ink">A pool of dishes, made for you.</div>
            <p className="text-sans_mute mt-4 max-w-2xl text-sm leading-relaxed">
              Mix and match freely within each meal. Every dish here has been chosen for your constitution and lifestyle by your Saans specialist —
              not by an algorithm. Recipe links open in a new tab.
            </p>
            {plan.doctor_name && (
              <div className="mt-6 text-xs text-sans_mute uppercase tracking-[0.22em]">
                Prepared by {plan.doctor_name}{plan.delivered_at ? ` · Delivered ${new Date(plan.delivered_at).toLocaleDateString('en-IN', { dateStyle: 'medium' })}` : ''}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => downloadPdf(plan.plan_id)}
            data-testid="diet-download-pdf-btn"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] bg-sans_ink text-sans_bone px-4 py-3 hover:opacity-90 transition-opacity"
          >
            <Download className="w-3.5 h-3.5" strokeWidth={1.75} /> Download PDF
          </button>
        </div>
      </div>

      {/* Sections */}
      <div className="mt-6 space-y-4">
        {nonEmpty.map((sec) => (
          <section key={sec.meal} className="border border-sans_line bg-white overflow-hidden">
            <div className="grid md:grid-cols-[220px_1fr]">
              <div className="p-6 md:p-8" style={{ background: MEAL_TINTS[sec.meal] || '#F4EFE7' }}>
                <div className="text-[10px] uppercase tracking-[0.22em] text-sans_ink/60">{sec.dishes.length} option{sec.dishes.length !== 1 ? 's' : ''}</div>
                <div className="font-serif text-2xl text-sans_ink mt-2">{sec.meal}</div>
              </div>
              <div className="divide-y divide-sans_line">
                {sec.dishes.map((d, i) => (
                  <div key={i} className="p-5 md:p-6 flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-serif text-lg text-sans_ink">{d.name}</div>
                      {d.notes && <div className="text-sm text-sans_mute mt-1 leading-relaxed max-w-xl">{d.notes}</div>}
                    </div>
                    {d.recipe_url && (
                      <a
                        href={d.recipe_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-sans_ink border border-sans_line px-3 py-2 hover:bg-sans_ink hover:text-sans_bone transition-colors inline-flex items-center gap-1.5"
                      >
                        {isYouTube(d.recipe_url) ? <PlayCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> : <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />}
                        Recipe
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        ))}
      </div>

      {/* Guidelines */}
      {(plan.general_guidelines || plan.foods_to_favour || plan.foods_to_avoid) && (
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          {plan.general_guidelines && (
            <div className="border border-sans_line bg-white p-6 md:col-span-3">
              <div className="sans-label mb-3">General guidelines</div>
              <p className="text-sans_ink whitespace-pre-wrap leading-relaxed">{plan.general_guidelines}</p>
            </div>
          )}
          {plan.foods_to_favour && (
            <div className="border border-sans_line bg-white p-6">
              <div className="sans-label mb-3" style={{ color: '#6E8B6E' }}>Favour</div>
              <p className="text-sm text-sans_ink whitespace-pre-wrap leading-relaxed">{plan.foods_to_favour}</p>
            </div>
          )}
          {plan.foods_to_avoid && (
            <div className="border border-sans_line bg-white p-6">
              <div className="sans-label mb-3" style={{ color: '#8B5A3C' }}>Avoid</div>
              <p className="text-sm text-sans_ink whitespace-pre-wrap leading-relaxed">{plan.foods_to_avoid}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DietPlanView;
